import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  CloseRounded,
  DirectionsCarOutlined,
  FilterAltOutlined,
  LocationOnOutlined,
  PaidOutlined,
  AddRounded,
  RefreshOutlined,
  SettingsOutlined,
  RouteOutlined,
  SearchOutlined,
  PersonOutlineOutlined,
  StopOutlined,
  NotesOutlined,
  SwapHorizRounded,
  LightbulbOutlined,
} from "@mui/icons-material";
import moment from "moment-timezone";

import DashboardClientLayout from "../../Layouts/DashboardClientLayout";
import BookingApi from "Services/Booking";
import RideApi from "Services/Ride";
import VehicleApi from "Services/Vehicle";
import PlaceService from "../../Services/Place";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import "./HomePage.css";

function getHistoryTabs(intl) {
  return [
    { label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.history.all" }), icon: "all", active: true, key: "all" },
    { label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.history.carpool" }), icon: "car", key: "covoiturage" },
    { label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.history.bus" }), icon: "bus", key: "bus" },
    { label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.history.train" }), icon: "train", key: "train" },
    { label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.history.delivery" }), icon: "box", key: "livraison" },
    { label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.history.errands" }), icon: "bag", key: "course" },
  ];
}

function getViewTabs(intl) {
  return [
    { key: "all", label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.view.all" }), icon: "all" },
    { key: "a_venir", label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.view.upcoming" }), icon: "clock" },
    { key: "passe", label: intl.formatMessage({ id: "dashboardClient.trajet.tabs.view.past" }), icon: "calendar" },
  ];
}

function getStatusOptions(intl) {
  return [
    { value: "all", label: intl.formatMessage({ id: "dashboardClient.trajet.status.all" }) },
    { value: "draft", label: intl.formatMessage({ id: "dashboardClient.trajet.status.draft" }) },
    { value: "published", label: intl.formatMessage({ id: "dashboardClient.trajet.status.published" }) },
    { value: "full", label: intl.formatMessage({ id: "dashboardClient.trajet.status.full" }) },
    { value: "cancelled", label: intl.formatMessage({ id: "dashboardClient.trajet.status.cancelled" }) },
    { value: "completed", label: intl.formatMessage({ id: "dashboardClient.trajet.status.completed" }) },
  ];
}

function getStatusLabelMap(intl) {
  return {
    pending: intl.formatMessage({ id: "dashboardClient.trajet.statusLabel.pending" }),
    confirmed: intl.formatMessage({ id: "dashboardClient.trajet.statusLabel.confirmed" }),
    cancelled: intl.formatMessage({ id: "dashboardClient.trajet.statusLabel.cancelled" }),
    completed: intl.formatMessage({ id: "dashboardClient.trajet.statusLabel.completed" }),
  };
}

const statusToneMap = {
  pending: "is-active",
  confirmed: "is-active",
  cancelled: "is-past",
  completed: "is-past",
};

const zoneForDate = (timezone) => timezone || "Africa/Lagos";

const getDateFns = (value, timezone) => {
  const date = value ? moment(value).tz(zoneForDate(timezone)) : null;
  return {
    dateLabel: date ? date.format("DD MMM YYYY") : "--",
    weekdayLabel: date ? date.format("dddd") : "--",
    timeLabel: date ? date.format("HH:mm") : "--:--",
  };
};

const toMoney = (value, currency = "XAF") => {
  if (value === null || value === undefined || value === "") {
    return `-- ${currency}`;
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return `${value} ${currency}`;
  }

  return `${new Intl.NumberFormat("fr-FR").format(amount)} ${currency}`;
};

const shortId = (value) => {
  if (!value) return "--";
  return value.length > 10 ? `${value.slice(0, 8)}...` : value;
};

const safeJoin = (...parts) => parts.filter(Boolean).join(" ");

const formatPlaceResult = (place, intl) => {
  const primary = place?.name || place?.addressName || place?.addressLocality || intl.formatMessage({ id: "dashboardClient.trajet.fallback.place" });
  const secondaryParts = [place?.addressLocality, place?.addressRegion, place?.addressCountry].filter(Boolean);

  return {
    id: place?.id || place?._id || primary,
    title: primary,
    subtitle: secondaryParts.join(", "),
  };
};

const normalizeRide = (item, section, index, intl) => {
  const source = item?.raw || item?.rides || item?.ride || item || {};
  const ride = source?.rides || source?.ride || source;
  const user = source?.users || source?.user || item?.users || item?.user || {};
  const driver = source?.driver || source?.users?.driver || {};
  const passengerFallback = intl.formatMessage({ id: "dashboardClient.trajet.fallback.passenger" });
  const passengerName =
    safeJoin(user?.first_name, user?.last_name) ||
    safeJoin(driver?.first_name, driver?.last_name) ||
    passengerFallback;
  const userFullName =
    safeJoin(user?.first_name, user?.last_name) ||
    safeJoin(driver?.first_name, driver?.last_name) ||
    passengerFallback;
  const vehicleNo =
    ride?.vehicle_no ||
    ride?.vehicles?.vehicle_no ||
    ride?.vehicle?.vehicle_no ||
    ride?.vehicleNumber ||
    ride?.plate_number ||
    "--";
  const vehicleName =
    ride?.vehicle_name ||
    ride?.vehicles?.vehicle_name ||
    ride?.vehicle?.vehicle_name ||
    ride?.vehicleModel ||
    ride?.vehicle_model ||
    "--";

  return {
    raw: source,
    id: source?.id || source?.ride_id || source?.created_at || `${section}-${index}`,
    bookingId: source?.booking_id || source?.bookingId || source?.id || "-",
    rideId: source?.ride_id || ride?.id || source?.id || "-",
    passengerName,
    userFullName,
    vehicleNo,
    vehicleName,
    phone: user?.phone || "-",
    originLabel:
      source?.origin?.addressName ||
      source?.origin?.name ||
      ride?.origin_place?.name ||
      ride?.origin_place?.label ||
      source?.origin_place?.name ||
      ride?.places_rides_origin_place_idToplaces?.name ||
      ride?.origin_place_name ||
      source?.origin_place_name ||
      intl.formatMessage({ id: "dashboardClient.trajet.fallback.origin" }),
    destinationLabel:
      source?.destination?.addressName ||
      source?.destination?.name ||
      ride?.destination_place?.name ||
      ride?.destination_place?.label ||
      source?.destination_place?.name ||
      ride?.places_rides_destination_place_idToplaces?.name ||
      ride?.destination_place_name ||
      source?.destination_place_name ||
      intl.formatMessage({ id: "dashboardClient.trajet.fallback.destination" }),
    departureAt: ride?.departure_at || source?.departure_at || source?.created_at || null,
    arrivalAt: ride?.estimated_arrival_at || null,
    totalAmount: source?.total_amount,
    pricePerSeat: source?.price_per_seat ?? ride?.price_per_seat ?? null,
    serviceFee: source?.service_fee,
    currency: source?.currency || ride?.currency || "XAF",
    rideType: source?.ride_type || ride?.ride_type || "-",
    comfortClass: source?.comfort_class || ride?.comfort_class || "-",
    rideWaypointsCount: Array.isArray(source?.ride_waypoints) ? source.ride_waypoints.length : 0,
    seatsBooked: source?.seats_booked ?? 0,
    totalSeats: ride?.total_seats ?? null,
    availableSeats: ride?.available_seats ?? null,
    bookingStatus: String(source?.status || "pending").toLowerCase(),
    rideStatus: String(ride?.status || "-").toLowerCase(),
    createdAt: source?.created_at || null,
    confirmedAt: source?.confirmed_at || null,
    cancellationReason: source?.cancellation_reason || "",
    driverId: ride?.driver_id || source?.driver_id || "-",
    section,
  };
};

const normalizeList = (list, section, intl) => {
  if (!Array.isArray(list)) return [];
  return list.map((item, index) => normalizeRide(item, section, index, intl));
};

const splitRidesByMoment = (list, timezone) => {
  const zone = zoneForDate(timezone);
  const now = moment().tz(zone);
  const upcoming = [];
  const past = [];

  list.forEach((trip) => {
    const departure = trip?.departureAt ? moment(trip.departureAt).tz(zone) : null;
    const isPastStatus = ["completed", "cancelled", "canceled"].includes(String(trip?.rideStatus || trip?.bookingStatus || "").toLowerCase());
    const isPastDate = departure ? departure.isBefore(now, "minute") : false;
    if (isPastStatus || isPastDate) {
      past.push(trip);
    } else {
      upcoming.push(trip);
    }
  });

  return { upcoming, past };
};

const normalizeRideResponse = (payload, timezone, intl) => {
  const data = payload?.data ?? payload ?? {};
  const list =
    Array.isArray(data?.rides) ? data.rides :
    Array.isArray(data?.data?.data) ? data.data.data :
    Array.isArray(data?.data?.rides) ? data.data.rides :
    Array.isArray(data?.a_venir) || Array.isArray(data?.passe) ? [] :
    Array.isArray(data) ? data : [];

  if (Array.isArray(data?.a_venir) || Array.isArray(data?.passe)) {
    return {
      upcoming: normalizeList(data?.a_venir || [], "a_venir", intl),
      past: normalizeList(data?.passe || [], "passe", intl),
    };
  }

  const normalized = normalizeList(list, "all", intl);
  return splitRidesByMoment(normalized, timezone);
};

const matchesSearch = (trip, term) => {
  if (!term) return true;
  const haystack = [
    trip?.bookingId,
    trip?.rideId,
    trip?.passengerName,
    trip?.userFullName,
    trip?.phone,
    trip?.originLabel,
    trip?.destinationLabel,
    trip?.bookingStatus,
    trip?.rideStatus,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(term.toLowerCase());
};

function TripTypeTab({ tab, active, onClick }) {
  return (
    <button type="button" className={`tc-trip-tab ${active ? "is-active" : ""}`} onClick={onClick}>
      <span className="tc-trip-tab-icon">
        <TripIcon kind={tab.icon} />
      </span>
      <span>{tab.label}</span>
    </button>
  );
}

function TripIcon({ kind }) {
  if (kind === "all") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 13.5 6.6 9.2A2.4 2.4 0 0 1 8.9 7.5h6.2a2.4 2.4 0 0 1 2.3 1.7L19 13.5" />
        <rect x="4" y="13.5" width="16" height="4.5" rx="1.5" />
        <circle cx="8" cy="18.5" r="1.2" />
        <circle cx="16" cy="18.5" r="1.2" />
      </svg>
    );
  }

  if (kind === "car") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 13.5 6.6 9.2A2.4 2.4 0 0 1 8.9 7.5h6.2a2.4 2.4 0 0 1 2.3 1.7L19 13.5" />
        <rect x="4" y="13.5" width="16" height="4.5" rx="1.5" />
        <circle cx="8" cy="18.5" r="1.2" />
        <circle cx="16" cy="18.5" r="1.2" />
      </svg>
    );
  }

  if (kind === "bus") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="5" y="5" width="14" height="13" rx="3" />
        <path d="M8 9h8M8 12h8M9 18v1M15 18v1" />
      </svg>
    );
  }

  if (kind === "train") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 4h10a3 3 0 0 1 3 3v7a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V7a3 3 0 0 1 3-3Z" />
        <path d="M8 9h8M8 12h8M9 18v1M15 18v1" />
      </svg>
    );
  }

  if (kind === "box") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m4.5 8 7.5-3.5L19.5 8 12 11.5 4.5 8Z" />
        <path d="M4.5 8v8l7.5 3.5 7.5-3.5V8" />
        <path d="M12 11.5v8" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 8h10l1 11H6L7 8Z" />
      <path d="M9 8a3 3 0 0 1 6 0" />
      <path d="M10 11h4" />
    </svg>
  );
}

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "grid", gap: 0.35 }}>
    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: "#10233f", wordBreak: "break-word" }}>
      {value || "--"}
    </Typography>
  </Box>
);

const TripDetailsDialog = ({ open, trip, timezone, onClose }) => {
  const intl = useIntl();
  const statusLabelMap = getStatusLabelMap(intl);

  if (!trip) return null;

  const departure = getDateFns(trip?.departureAt, timezone);
  const arrival = trip?.arrivalAt ? getDateFns(trip.arrivalAt, timezone) : null;
  const bookingStatusLabel = statusLabelMap[trip?.bookingStatus] || trip?.bookingStatus || "--";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#10233f" }}>
              {intl.formatMessage({ id: "dashboardClient.trajet.details.title" })}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              {trip?.originLabel} - {trip?.destinationLabel}
            </Typography>
          </Box>
          <Chip
            label={bookingStatusLabel}
            color={trip?.bookingStatus === "cancelled" ? "error" : trip?.bookingStatus === "completed" ? "success" : "primary"}
            variant="outlined"
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#f8fafc" }}>
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid rgba(15, 39, 72, 0.08)",
              background: "#fff",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
              <Box>
                <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.16em" }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.details.passenger" })}
                </Typography>
                <Typography variant="h6" sx={{ color: "#10233f" }}>
                  {trip?.passengerName}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {trip?.phone}
                </Typography>
              </Box>

              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography variant="overline" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.16em" }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.details.amount" })}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#0f6a3b" }}>
                  {toMoney(trip?.totalAmount, trip?.currency)}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {intl.formatMessage(
                    { id: "dashboardClient.trajet.details.serviceFee" },
                    { amount: toMoney(trip?.serviceFee, trip?.currency) }
                  )}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3.5,
              border: "1px solid #e6ebf1",
              background: "#fff",
              p: 1.75,
              boxShadow: "none",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#334155", fontSize: 16, mb: 1.25 }}>
              {intl.formatMessage({ id: "dashboardClient.trajet.details.tripInfo" })}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailRow
                  label={intl.formatMessage({ id: "dashboardClient.trajet.details.departure" })}
                  value={intl.formatMessage(
                    { id: "dashboardClient.trajet.details.departureValue" },
                    { date: departure.dateLabel, time: departure.timeLabel }
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow
                  label={intl.formatMessage({ id: "dashboardClient.trajet.details.estimatedArrival" })}
                  value={
                    arrival
                      ? intl.formatMessage(
                          { id: "dashboardClient.trajet.details.departureValue" },
                          { date: arrival.dateLabel, time: arrival.timeLabel }
                        )
                      : "--"
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow label={intl.formatMessage({ id: "dashboardClient.trajet.details.status" })} value={bookingStatusLabel} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow
                  label={intl.formatMessage({ id: "dashboardClient.trajet.details.reference" })}
                  value={shortId(trip?.bookingId || trip?.rideId || trip?.id)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow label={intl.formatMessage({ id: "dashboardClient.trajet.details.seats" })} value={trip?.totalSeats ?? "--"} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailRow
                  label={intl.formatMessage({ id: "dashboardClient.trajet.details.comfortClass" })}
                  value={trip?.comfortClass || "--"}
                />
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const CreateTripDialog = ({ open, onClose, timezone, onCreated }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [placeList, setPlaceList] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [originPlaceId, setOriginPlaceId] = useState("");
  const [destinationPlaceId, setDestinationPlaceId] = useState("");
  const [departureDateTime, setDepartureDateTime] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceStartDate, setRecurrenceStartDate] = useState("");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [price, setPrice] = useState("");
  const [totalSeats, setTotalSeats] = useState("");
  const [notes, setNotes] = useState("");
  const [waypointDraft, setWaypointDraft] = useState("");
  const [waypoints, setWaypoints] = useState([]);

  const placeOptions = useMemo(
    () =>
      placeList
        .map((place) => {
          const id = place?.id || place?._id;
          if (!id) return null;
          return {
            id,
            label: formatPlaceResult(place, intl).title,
            subtitle: formatPlaceResult(place, intl).subtitle,
          };
        })
        .filter(Boolean),
    [placeList, intl]
  );

  const selectedOriginPlace = useMemo(
    () => placeOptions.find((place) => place.id === originPlaceId) || null,
    [placeOptions, originPlaceId]
  );

  const selectedDestinationPlace = useMemo(
    () => placeOptions.find((place) => place.id === destinationPlaceId) || null,
    [placeOptions, destinationPlaceId]
  );

  const vehicleOptions = useMemo(
    () =>
      vehicleList
        .map((item) => {
          const id = item?.id || item?._id;
          if (!id) return null;
          return {
            id,
            label:
              item?.vehicle_name ||
              item?.name ||
              item?.vehicle_no ||
              item?.plate_number ||
              item?.registration_number ||
              intl.formatMessage({ id: "dashboardClient.trajet.fallback.vehicle" }),
          };
        })
        .filter(Boolean),
    [vehicleList, intl]
  );

  const resetForm = useCallback(() => {
    setSubmitError("");
    setOriginPlaceId("");
    setDestinationPlaceId("");
    setDepartureDateTime("");
    setIsRecurring(false);
    setRecurrenceStartDate("");
    setRecurrenceEndDate("");
    setVehicle("");
    setPrice("");
    setTotalSeats("");
    setNotes("");
    setWaypointDraft("");
    setWaypoints([]);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }

    let active = true;
    setPlaceLoading(true);
    setVehicleLoading(true);

    Promise.all([
      PlaceService.getPlaceList({ page: 1, limit: "unlimited", search: "" }),
      VehicleApi.getVehicleList({ page: 1, limit: "unlimited", search: "" }),
    ])
      .then(([placesRes, vehiclesRes]) => {
        if (!active) return;
        const places = placesRes?.data?.data?.data || placesRes?.data?.data || placesRes?.data?.places || placesRes?.data || [];
        const vehicles = vehiclesRes?.data?.data?.data || vehiclesRes?.data?.data || vehiclesRes?.data?.vehicles || vehiclesRes?.data || [];
        setPlaceList(Array.isArray(places) ? places : []);
        setVehicleList(Array.isArray(vehicles) ? vehicles : []);
      })
      .catch(() => {
        if (!active) return;
        setPlaceList([]);
        setVehicleList([]);
      })
      .finally(() => {
        if (!active) return;
        setPlaceLoading(false);
        setVehicleLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, resetForm]);

  const handleAddWaypoint = () => {
    const next = waypointDraft.trim();
    if (!next) return;
    setWaypoints((current) => [...current, next]);
    setWaypointDraft("");
  };

  const handleRecurringToggle = (checked) => {
    setIsRecurring(checked);
    if (checked && departureDateTime && !recurrenceStartDate) {
      setRecurrenceStartDate(departureDateTime);
    }
  };

  const handleCreateRide = async () => {
    if (!originPlaceId || !destinationPlaceId) {
      setSubmitError(intl.formatMessage({ id: "dashboardClient.trajet.create.validation.missingPlaces" }));
      return;
    }

    if (!departureDateTime) {
      setSubmitError(intl.formatMessage({ id: "dashboardClient.trajet.create.validation.missingDateTime" }));
      return;
    }

    if (isRecurring && (!recurrenceStartDate || !recurrenceEndDate)) {
      setSubmitError(intl.formatMessage({ id: "dashboardClient.trajet.create.validation.missingRecurrenceDates" }));
      return;
    }

    if (!totalSeats) {
      setSubmitError(intl.formatMessage({ id: "dashboardClient.trajet.create.validation.missingSeats" }));
      return;
    }

    setSubmitError("");
    setSubmitting(true);
    dispatch(showLoader(intl.formatMessage({ id: "dashboardClient.trajet.create.loader" })));

    try {
      const departureAt = moment.tz(departureDateTime, "YYYY-MM-DDTHH:mm", zoneForDate(timezone));

      const payload = {
        origin_place_id: originPlaceId,
        destination_place_id: destinationPlaceId,
        departure_at: departureAt.toISOString(),
        estimated_arrival_at: departureAt.clone().add(1, "hour").toISOString(),
        total_seats: Number(totalSeats),
        price_per_seat: price ? Number(price) : undefined,
        vehicle_id: vehicle || undefined,
        notes: notes || undefined,
        status: "draft",
        is_recurring: Boolean(isRecurring),
        recurrence_start_date: isRecurring && recurrenceStartDate
          ? moment.tz(recurrenceStartDate, "YYYY-MM-DDTHH:mm", zoneForDate(timezone)).toISOString()
          : undefined,
        recurrence_end_date: isRecurring && recurrenceEndDate
          ? moment.tz(recurrenceEndDate, "YYYY-MM-DDTHH:mm", zoneForDate(timezone)).toISOString()
          : undefined,
      };

      if (waypoints.length) {
        payload.waypoints = waypoints.map((item, index) => ({
          waypoint_place_id: item,
          waypoint_sequence_order: index + 1,
        }));
      }

      await RideApi.createRideV2(payload);
      dispatch(
        showSnackbar({
          message: intl.formatMessage({ id: "dashboardClient.trajet.create.successMessage" }),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "success",
        })
      );
      resetForm();
      onCreated?.();
      onClose?.();
    } catch (error) {
      const message = error?.response?.data?.message || intl.formatMessage({ id: "dashboardClient.trajet.create.errorMessage" });
      setSubmitError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 5000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setSubmitting(false);
      dispatch(hideLoader());
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      scroll="paper"
      PaperProps={{
        sx: {
          fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          borderRadius: 2.5,
          overflow: "hidden",
          background: "#f8fafc",
          boxShadow: "0 18px 50px rgba(15, 23, 42, 0.14)",
          border: "1px solid rgba(15, 23, 42, 0.06)",
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, md: 2.5 }, py: 1.25, borderBottom: "1px solid #e5edf4", background: "#fff" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                background: "linear-gradient(180deg, #0f8a48 0%, #0b6e39 100%)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                boxShadow: "0 10px 24px rgba(11, 122, 69, 0.18)",
              }}
            >
              <RouteOutlined sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f2937", lineHeight: 1.1 }}>
                {intl.formatMessage({ id: "dashboardClient.trajet.create.title" })}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: 13, lineHeight: 1.1 }}>
                {intl.formatMessage({ id: "dashboardClient.trajet.create.subtitle" })}
              </Typography>
            </Box>
          </Stack>

          <Button
            onClick={onClose}
            sx={{
              minWidth: 32,
              width: 32,
              height: 32,
              borderRadius: 1.5,
              p: 0,
              color: "#64748b",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              "&:hover": { background: "#eef2f7", boxShadow: "none" },
            }}
          >
            <CloseRounded fontSize="small" />
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, md: 1.75 }, background: "#f4f7fb" }}>
        <Stack spacing={1.5} sx={{ fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.95fr 1fr" }, gap: 1.5, alignItems: "stretch" }}>
            <Stack spacing={1.25} sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e7edf4",
                  background: "#fff",
                  p: 1.75,
                  boxShadow: "none",
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(180deg, #1fa85c 0%, #0f8a48 100%)", color: "#fff", display: "grid", placeItems: "center", boxShadow: "0 10px 22px rgba(11, 122, 69, 0.18)", flexShrink: 0 }}>
                    <RouteOutlined fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.1 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.title" })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b", fontSize: 13 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.helperTitle" })}
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={1.75} alignItems="flex-start">
                  <Grid item xs={12} md={5.5}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.45 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.pickupLabel" })}
                    </Typography>
                    <Autocomplete
                      options={placeOptions}
                      value={selectedOriginPlace}
                      onChange={(_, newValue) => setOriginPlaceId(newValue?.id || "")}
                      loading={placeLoading}
                      disabled={placeLoading}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      noOptionsText={intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.noResults" })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder={intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.pickupPlaceholder" })}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <LocationOnOutlined sx={{ color: "#0b7a45" }} fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <InputAdornment position="end" sx={{ gap: 0.4 }}>
                                {placeLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                <SearchOutlined sx={{ color: "#94a3b8" }} fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff", pr: 0.5 },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", pt: 3.0 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => {
                        setOriginPlaceId(destinationPlaceId);
                        setDestinationPlaceId(originPlaceId);
                      }}
                      sx={{
                        minWidth: 40,
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        borderColor: "#dbe3ea",
                        color: "#0b7a45",
                        background: "#fff",
                        boxShadow: "none",
                        "&:hover": { background: "#f6fbf7", borderColor: "#bfe4cc" },
                      }}
                    >
                      <SwapHorizRounded fontSize="small" />
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={5.5}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.45 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.dropoffLabel" })}
                    </Typography>
                    <Autocomplete
                      options={placeOptions}
                      value={selectedDestinationPlace}
                      onChange={(_, newValue) => setDestinationPlaceId(newValue?.id || "")}
                      loading={placeLoading}
                      disabled={placeLoading}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      noOptionsText={intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.noResults" })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder={intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.dropoffPlaceholder" })}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <LocationOnOutlined sx={{ color: "#0b7a45" }} fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <InputAdornment position="end" sx={{ gap: 0.4 }}>
                                {placeLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                <SearchOutlined sx={{ color: "#94a3b8" }} fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff", pr: 0.5 },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e7edf4",
                  background: "#fff",
                  p: 1.75,
                  boxShadow: "none",
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(180deg, #1fa85c 0%, #0f8a48 100%)", color: "#fff", display: "grid", placeItems: "center", boxShadow: "0 10px 22px rgba(11, 122, 69, 0.18)", flexShrink: 0 }}>
                    <CalendarMonthOutlined fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.1 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.datetime.title" })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b", fontSize: 13 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.datetime.helper" })}
                    </Typography>
                  </Box>
                </Stack>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="datetime-local"
                      size="small"
                      fullWidth
                      value={departureDateTime}
                      onChange={(event) => setDepartureDateTime(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff", minHeight: 50 },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                        }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e7edf4",
                  background: "#fff",
                  p: 1.75,
                  boxShadow: "none",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                  <Checkbox
                    checked={isRecurring}
                    onChange={(event) => handleRecurringToggle(event.target.checked)}
                    sx={{ color: "#0b7a45", "&.Mui-checked": { color: "#0b7a45" }, p: 0.25, mt: 0.15 }}
                  />
                  <Box>
                    <Typography sx={{ color: "#10233f", fontWeight: 800, fontSize: 16, lineHeight: 1.15 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.recurrence.label" })}
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: 13, lineHeight: 1.2, mt: 0.35 }}>
                      {intl.formatMessage({ id: "dashboardClient.trajet.create.recurrence.helper" })}
                    </Typography>
                  </Box>
                </Box>
                {isRecurring ? (
                  <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.45 }}>
                        {intl.formatMessage({ id: "dashboardClient.trajet.create.recurrence.startLabel" })}
                      </Typography>
                      <TextField
                        type="datetime-local"
                        size="small"
                        fullWidth
                        value={recurrenceStartDate}
                        onChange={(event) => setRecurrenceStartDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.45 }}>
                        {intl.formatMessage({ id: "dashboardClient.trajet.create.recurrence.endLabel" })}
                      </Typography>
                      <TextField
                        type="datetime-local"
                        size="small"
                        fullWidth
                        value={recurrenceEndDate}
                        onChange={(event) => setRecurrenceEndDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                        }}
                      />
                    </Grid>
                  </Grid>
                ) : null}
              </Paper>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e7edf4",
                background: "#fff",
                p: 1.75,
                boxShadow: "none",
                height: "100%",
              }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(180deg, #1fa85c 0%, #0f8a48 100%)", color: "#fff", display: "grid", placeItems: "center", boxShadow: "0 10px 22px rgba(11, 122, 69, 0.18)", flexShrink: 0 }}>
                    <NotesOutlined fontSize="small" />
                  </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.1 }}>
                    {intl.formatMessage({ id: "dashboardClient.trajet.create.details.title" })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", fontSize: 13 }}>
                    {intl.formatMessage({ id: "dashboardClient.trajet.create.details.helperTitle" })}
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.45 }}>
                {intl.formatMessage({ id: "dashboardClient.trajet.create.details.notesLabel" })}
              </Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={6}
                placeholder={intl.formatMessage({ id: "dashboardClient.trajet.create.details.notesPlaceholder" })}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.2, mr: 1 }}>
                      <NotesOutlined sx={{ color: "#64748b" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: "#fff",
                    alignItems: "flex-start",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                }}
              />
              <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", textAlign: "right", mt: 0.4 }}>
                {notes.length}/500
              </Typography>

              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1 }}>
                {intl.formatMessage({ id: "dashboardClient.trajet.create.details.notesHelper" })}
              </Typography>

              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 800, mb: 0.75 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.details.totalSeatsLabel" })}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  placeholder={intl.formatMessage({ id: "dashboardClient.trajet.create.details.totalSeatsPlaceholder" })}
                  value={totalSeats}
                  onChange={(event) => setTotalSeats(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlined sx={{ color: "#0b7a45" }} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                  }}
                />
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.75 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.details.totalSeatsHelper" })}
                </Typography>
              </Box>
            </Paper>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              background: "#fff",
              p: 1.5,
              boxShadow: "none",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(180deg, #1fa85c 0%, #0f8a48 100%)",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  boxShadow: "0 10px 22px rgba(11, 122, 69, 0.18)",
                  flexShrink: 0,
                }}
              >
                <SettingsOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.1 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.config.title" })}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", fontSize: 13 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.config.helperTitle" })}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.45 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.config.vehicleLabel" })}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  select
                  value={vehicle}
                  onChange={(event) => setVehicle(event.target.value)}
                  disabled={vehicleLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                  }}
                >
                  <MenuItem value="">
                    {vehicleLoading
                      ? intl.formatMessage({ id: "dashboardClient.trajet.create.itinerary.loading" })
                      : intl.formatMessage({ id: "dashboardClient.trajet.create.config.vehiclePlaceholder" })}
                  </MenuItem>
                  {vehicleOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.45 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.config.vehicleHelper" })}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.45 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.config.priceLabel" })}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  placeholder="0"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">XAF</InputAdornment>,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ea" },
                  }}
                />
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.45 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.config.priceHelper" })}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #d9f0df",
              background: "linear-gradient(180deg, #f4fbf6 0%, #eef9f1 100%)",
              p: 1.5,
              boxShadow: "none",
              minHeight: 112,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "#e7f6ea",
                  color: "#0b7a45",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <LightbulbOutlined sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ color: "#0f7a45", fontWeight: 800 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.tip.title" })}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25, fontSize: 13 }}>
                  {intl.formatMessage({ id: "dashboardClient.trajet.create.tip.body" })}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {submitError ? (
            <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: 700, px: 0.5 }}>
              {submitError}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.5,
          background: "#fff",
          borderTop: "1px solid #e5edf4",
          gap: 1.25,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            minWidth: 92,
            height: 38,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            color: "#334155",
            borderColor: "#d8e0e7",
            background: "#fff",
            "&:hover": { background: "#f8fafc", borderColor: "#c8d0d9" },
          }}
        >
          {intl.formatMessage({ id: "dashboardClient.trajet.create.cancelButton" })}
        </Button>
        <Button
          variant="contained"
          disabled={submitting}
          onClick={handleCreateRide}
          sx={{
            minWidth: 168,
            height: 46,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            background: submitting ? "#94a3b8" : "linear-gradient(135deg, #0f8a48 0%, #0b6e39 100%)",
            "&:hover": {
              boxShadow: "none",
              background: submitting ? "#94a3b8" : "linear-gradient(135deg, #0d7a40 0%, #0a6233 100%)",
            },
          }}
        >
          {submitting
            ? intl.formatMessage({ id: "dashboardClient.trajet.create.submitting" })
            : intl.formatMessage({ id: "dashboardClient.trajet.create.submitButton" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TripRow = ({ trip, timezone, onOpenDetails, onOpenTicket, isTicketLoading }) => {
  const intl = useIntl();
  const statusLabelMap = getStatusLabelMap(intl);
  const { dateLabel, weekdayLabel } = getDateFns(trip?.departureAt, timezone);
  const departureAtLabel = trip?.departureAt ? getDateFns(trip?.departureAt, timezone).timeLabel : "--:--";
  const arrivalAtLabel = trip?.arrivalAt ? getDateFns(trip?.arrivalAt, timezone).timeLabel : "--:--";
  const bookingLabel = statusLabelMap[trip?.bookingStatus] || trip?.bookingStatus || intl.formatMessage({ id: "dashboardClient.trajet.details.reference" });
  const bookingTone = statusToneMap[trip?.bookingStatus] || "is-active";

  return (
    <article className="tc-trip-row">
      <div className="tc-trip-date">
        <span>{weekdayLabel}</span>
        <strong>{dateLabel}</strong>
      </div>

      <div className="tc-trip-times">
        <div className="tc-trip-rail" aria-hidden="true">
          <span />
        </div>
      </div>

      <div className="tc-trip-route">
        <div className="tc-trip-stop">
          <strong>{trip?.originLabel}</strong>
        </div>
        <div className="tc-trip-stop tc-trip-stop-end">
          <strong>{trip?.destinationLabel}</strong>
        </div>
      </div>

      <div className="tc-trip-mode">
        <span className="tc-trip-mode-icon" aria-hidden="true">
          <StopOutlined fontSize="small" />
        </span>
        <span className="tc-trip-mode-text">
          <strong>{intl.formatMessage({ id: "dashboardClient.trajet.row.stopsLabel" })}</strong>
          <span>{trip?.rideWaypointsCount > 0 ? trip.rideWaypointsCount : "--"}</span>
        </span>
      </div>

      <div className="tc-trip-mode">
        <span className="tc-trip-mode-icon" aria-hidden="true">
          <DirectionsCarOutlined fontSize="small" />
        </span>
        <span className="tc-trip-mode-text">
          <strong>{trip?.vehicleNo || "--"}</strong>
          <span>{trip?.vehicleName || "--"}</span>
        </span>
      </div>

      <div className="tc-trip-price">
        <div className="tc-trip-price-tags">
          <span className={bookingTone}>{bookingLabel}</span>
          <span className={bookingTone}>{trip?.rideType || "--"}</span>
          <span className={bookingTone}>{trip?.comfortClass || "--"}</span>
        </div>
      </div>

      <div className="tc-trip-price">
        <strong>
          {intl.formatMessage(
            { id: "dashboardClient.trajet.row.pricePerSeat" },
            { price: toMoney(trip?.pricePerSeat ?? trip?.raw?.price_per_seat, trip?.currency) }
          )}
        </strong>
        <span>
          {" "}
          {intl.formatMessage(
            { id: "dashboardClient.trajet.row.availableSeats" },
            { count: trip?.availableSeats }
          )}
        </span>
      </div>

      <button type="button" className="tc-trip-detail-btn" onClick={() => onOpenTicket(trip)}>
        {intl.formatMessage({ id: "dashboardClient.trajet.row.detailButton" })}
      </button>
      <button
        type="button"
        className="tc-trip-more-btn"
        onClick={() => onOpenDetails(trip)}
        aria-label={intl.formatMessage({ id: "dashboardClient.trajet.row.moreAriaLabel" })}
      >
        <span />
        <span />
        <span />
      </button>
    </article>
  );
};

const TripSection = ({ title, items, timezone, onOpenDetails, onOpenTicket, ticketLoadingId }) => {
  if (!items.length) return null;

  return (
    <section className="tc-trip-section">
      <h2>{title}</h2>
      <div className="tc-trip-list">
        {items.map((trip) => (
          <TripRow
            key={trip.id}
            trip={trip}
            timezone={timezone}
            onOpenDetails={onOpenDetails}
            onOpenTicket={onOpenTicket}
            isTicketLoading={ticketLoadingId === trip.id}
          />
        ))}
      </div>
    </section>
  );
};

const TripsTableSection = ({ title, items, onOpenDetails, onOpenTicket, ticketLoadingId, loading }) => {
  const intl = useIntl();
  const [pageSize, setPageSize] = useState(5);

  const renderTextCell = useCallback(
    (value) => (
      <Typography variant="body2" noWrap title={value ?? "--"} sx={{ width: "100%" }}>
        {value ?? "--"}
      </Typography>
    ),
    []
  );

  const normalizedRows = useMemo(
    () =>
      items.map((trip, index) => ({
        ...trip,
        sno: index + 1,
      })),
    [items]
  );

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.date" }),
        minWidth: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const rawDate = params?.row?.departureAt;
          const timeLabel = rawDate ? moment(rawDate).locale("fr").format("HH:mm") : "--:--";
          const formattedDate = rawDate ? moment(rawDate).locale("fr").format("DD MMM YYYY").replace(".", "") : "--";
          const dateParts = formattedDate.split(" ");
          const dateLabel = dateParts.length === 3
            ? dateParts[0] + " " + dateParts[1].charAt(0).toUpperCase() + dateParts[1].slice(1) + " " + dateParts[2]
            : formattedDate;

          return (
            <Box sx={{ display: "grid", gap: 0.1, minWidth: 0, lineHeight: 1.1 }}>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, lineHeight: 1.1 }}>
                {timeLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                {dateLabel}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "departure",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.type" }),
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "grid", gap: 0.15, minWidth: 0 }}>
            <Typography variant="caption" noWrap title={`${params?.row?.rideType || "--"} `} sx={{ color: "#64748b", fontWeight: 600, lineHeight: 1.15 }}>
              {params?.row?.rideType || "--"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "route",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.route" }),
        minWidth: 230,
        flex: 1.3,
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "grid", gap: 0.1, minWidth: 0, lineHeight: 1.1 }}>
            <Typography
              variant="body2"
              title={params?.row?.originLabel || "--"}
              sx={{ color: "#10233f", fontWeight: 700, lineHeight: 1.1, whiteSpace: "normal" }}
            >
              {params?.row?.originLabel || "--"}
            </Typography>
            <Typography
              variant="body2"
              title={params?.row?.destinationLabel || "--"}
              sx={{ color: "#10233f", fontWeight: 600, lineHeight: 1.1, whiteSpace: "normal" }}
            >
              {params?.row?.destinationLabel || "--"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "stops",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.stops" }),
        minWidth: 110,
        flex: 0.8,
        sortable: true,
        renderCell: (params) => renderTextCell(params?.row?.rideWaypointsCount > 0 ? params?.row?.rideWaypointsCount : "--"),
      },
      {
        field: "vehicle",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.vehicle" }),
        minWidth: 170,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "grid", gap: 0.15, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={params?.row?.vehicleNo || "--"} sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.15 }}>
              {params?.row?.vehicleNo || "--"}
            </Typography>
            <Typography variant="caption" noWrap title={params?.row?.vehicleName || "--"} sx={{ color: "#64748b", fontWeight: 600, lineHeight: 1.15 }}>
              {params?.row?.vehicleName || "--"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "info",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.comfort" }),
        minWidth: 180,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "grid", gap: 0.15, minWidth: 0 }}>
            <Typography variant="caption" noWrap title={`${params?.row?.comfortClass || "--"}`} sx={{ color: "#64748b", fontWeight: 600, lineHeight: 1.15 }}>
              {params?.row?.comfortClass || "--"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "rideStatus",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.status" }),
        minWidth: 140,
        flex: 0.9,
        sortable: true,
        renderCell: (params) => (
          <span className={`tc-status-pill tc-status-pill--${params?.row?.statusTone || "is-active"}`}>
            {params?.row?.rideStatus || "--"}
          </span>
        ),
      },
      {
        field: "seats",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.seats" }),
        minWidth: 110,
        flex: 0.75,
        sortable: true,
        renderCell: (params) =>
          renderTextCell(`${params?.row?.availableSeats ?? "--"}/${params?.row?.totalSeats || "--"}`),
      },
      {
        field: "price",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.price" }),
        minWidth: 130,
        flex: 0.9,
        sortable: true,
        renderCell: (params) =>
          renderTextCell(
            intl.formatMessage(
              { id: "dashboardClient.trajet.table.pricePerSeat" },
              { price: toMoney(params?.row?.pricePerSeat ?? params?.row?.raw?.price_per_seat, params?.row?.currency) }
            )
          ),
      },
      {
        field: "detailsAction",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.detail" }),
        minWidth: 150,
        flex: 0.95,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <button
            type="button"
            className="tc-trip-detail-btn"
            onClick={(event) => {
              event.stopPropagation();
              onOpenDetails?.(params?.row);
            }}
          >
            {intl.formatMessage({ id: "dashboardClient.trajet.table.detailButton" })}
          </button>
        ),
      },
      {
        field: "ticketAction",
        headerName: intl.formatMessage({ id: "dashboardClient.trajet.table.columns.ticket" }),
        minWidth: 170,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <button
            type="button"
            className="tc-trip-ticket-btn"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}
            disabled={ticketLoadingId === params?.row?.id}
            onClick={(event) => {
              event.stopPropagation();
              onOpenTicket?.(params?.row);
            }}
          >
            {ticketLoadingId === params?.row?.id
              ? intl.formatMessage({ id: "dashboardClient.trajet.table.ticketLoading" })
              : intl.formatMessage({ id: "dashboardClient.trajet.table.ticketButton" })}
          </button>
        ),
      },
    ],
    [intl, onOpenDetails, onOpenTicket, renderTextCell, ticketLoadingId]
  );

  if (!items.length) return null;

  return (
    <section className="tc-trip-section">
      <h2>{title}</h2>
      <Paper
        elevation={0}
        className="tc-table-card"
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(15, 39, 72, 0.08)",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <DataGrid
          autoHeight
          rows={normalizedRows}
          columns={columns}
          loading={loading}
          disableSelectionOnClick
          disableColumnMenu
          density="compact"
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 25, 50]}
          rowHeight={72}
          headerHeight={56}
          getRowId={(row) => row.id || row.rideId || row.bookingId || row.sno}
          localeText={{ noRowsLabel: intl.formatMessage({ id: "dashboardClient.trajet.table.noRows" }) }}
          className="tc-data-grid-table"
          onRowClick={(params) => onOpenDetails?.(params?.row)}
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
          }}
        />
      </Paper>
    </section>
  );
};

void TripRow;
void TripSection;

const StatCard = ({ label, value, helper, icon }) => (
  <Paper
    elevation={0}
    className="tc-summary-card"
    sx={{
      borderRadius: 3,
      border: "1px solid rgba(15, 39, 72, 0.08)",
      background: "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(243,248,251,0.92))",
      p: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "14px",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(180deg, #0f6a3b, #0a532e)",
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, letterSpacing: "0.04em" }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
        {helper}
      </Typography>
    </Box>
  </Paper>
);

export default function TrajetClient() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useSelector((state) => state?.timezone?.timezone);
  const passengerId = useSelector((state) => state?.AuthReducer?.userid || localStorage.getItem("userid") || "");

  const historyTabs = getHistoryTabs(intl);
  const statusOptions = getStatusOptions(intl);

  const [selectedView, setSelectedView] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [upcomingTrips, setUpcomingTrips] = useState([]);
  const [pastTrips, setPastTrips] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [ticketLoadingId, setTicketLoadingId] = useState("");
  const [createTripOpen, setCreateTripOpen] = useState(false);

  const loadPassengerRides = useCallback(async () => {
    if (!passengerId) {
      setUpcomingTrips([]);
      setPastTrips([]);
      setLoadError(intl.formatMessage({ id: "dashboardClient.trajet.errors.noUserId" }));
      return;
    }

    setIsLoading(true);
    setLoadError("");
    dispatch(showLoader(intl.formatMessage({ id: "dashboardClient.trajet.loading.trips" })));

    try {
      const params = { driver_id: passengerId, page: 1, limit: "unlimited" };
      if (selectedDate) params.date = selectedDate;
      if (selectedStatus !== "all") params.status = selectedStatus;

      const res = await RideApi.getRideList(params);
      if (res?.data) {
        const { upcoming, past } = normalizeRideResponse(res?.data, timezone, intl);
        setUpcomingTrips(upcoming);
        setPastTrips(past);
        setLoadedAt(new Date());
      } else {
        setUpcomingTrips([]);
        setPastTrips([]);
        setLoadError(res?.data?.message || intl.formatMessage({ id: "dashboardClient.trajet.errors.noTripsFound" }));
      }
    } catch (error) {
      setUpcomingTrips([]);
      setPastTrips([]);
      const message = error?.response?.data?.message || intl.formatMessage({ id: "dashboardClient.trajet.errors.loadFailed" });
      setLoadError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setIsLoading(false);
      dispatch(hideLoader());
    }
  }, [dispatch, passengerId, selectedDate, selectedStatus, timezone, intl]);

  useEffect(() => {
    loadPassengerRides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleUpcoming = useMemo(
    () => upcomingTrips.filter((trip) => matchesSearch(trip, searchTerm)),
    [searchTerm, upcomingTrips]
  );
  const visiblePast = useMemo(
    () => pastTrips.filter((trip) => matchesSearch(trip, searchTerm)),
    [pastTrips, searchTerm]
  );

  const visibleTrips = useMemo(() => {
    if (selectedView === "a_venir") {
      return { upcoming: visibleUpcoming, past: [] };
    }

    if (selectedView === "passe") {
      return { upcoming: [], past: visiblePast };
    }

    return { upcoming: visibleUpcoming, past: visiblePast };
  }, [selectedView, visiblePast, visibleUpcoming]);

  const totalTrips = upcomingTrips.length + pastTrips.length;
  const filteredTripsCount = visibleTrips.upcoming.length + visibleTrips.past.length;
  const pendingCount = [...upcomingTrips, ...pastTrips].filter((trip) => trip.bookingStatus === "pending").length;
  const confirmedCount = [...upcomingTrips, ...pastTrips].filter((trip) => trip.bookingStatus === "confirmed").length;
  const completedCount = [...upcomingTrips, ...pastTrips].filter((trip) => trip.bookingStatus === "completed").length;

  const activeStatusLabel =
    statusOptions.find((option) => option.value === selectedStatus)?.label ||
    intl.formatMessage({ id: "dashboardClient.trajet.status.all" });
  const lastUpdatedLabel = loadedAt
    ? moment(loadedAt).tz(zoneForDate(timezone)).format("DD/MM/YYYY HH:mm")
    : "--";

  const openDetails = useCallback((trip) => {
    setSelectedTrip(trip);
    setDetailsOpen(true);
  }, []);

  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
  }, []);

  const openBookingTicket = useCallback(
    (trip) => {
      navigate("/corporate-courses", {
        state: {
          driver_id: trip?.driverId || trip?.raw?.driver_id || trip?.raw?.driver?.id || "",
          id: trip?.rideId || trip?.id || trip?.raw?.ride_id || trip?.raw?.id || "",
        },
      });
    },
    [navigate]
  );

  const openCreateTrip = useCallback(() => {
    setCreateTripOpen(true);
  }, []);

  const closeCreateTrip = useCallback(() => {
    setCreateTripOpen(false);
  }, []);

  return (
    <>
      <Box className="tc-content" sx={{ display: "grid", gap: 2.25, pb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2.25, md: 3 },
            border: "1px solid rgba(15, 39, 72, 0.08)",
            background: "linear-gradient(135deg, rgb(19 145 83 / 97%) 0%, rgb(25 103 16 / 98%) 100%)",
            color: "#fff",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: "auto -20px -30px auto",
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05, mt: 0.5 }}>
                {intl.formatMessage({ id: "dashboardClient.trajet.header.title" })}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, maxWidth: 760, color: "rgba(255,255,255,0.88)" }}>
                {intl.formatMessage({ id: "dashboardClient.trajet.header.subtitle" })}
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                minWidth: { xs: "100%", md: 210 },
                borderRadius: 3,
                p: 2,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(10px)",
                color: "#fff",
              }}
            >
              <button type="button" className="tc-trip-detail-btn" onClick={openCreateTrip}>
                {intl.formatMessage({ id: "dashboardClient.trajet.header.newTripButton" })}
              </button>
            </Paper>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 2, md: 2.5 },
            border: "1px solid rgba(15, 39, 72, 0.08)",
            background: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", xl: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", xl: "center" }}
            justifyContent="space-between"
          >
            <Box className="tc-trip-tabs" sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>
              {historyTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`tc-trip-tab ${selectedView === tab.key ? "is-active" : ""}`}
                  onClick={() => setSelectedView(tab.key)}
                >
                  <span className="tc-trip-tab-icon">
                    {tab.icon === "clock" ? (
                      <AccessTimeOutlined fontSize="small" />
                    ) : tab.icon === "calendar" ? (
                      <CalendarMonthOutlined fontSize="small" />
                    ) : (
                      <RouteOutlined fontSize="small" />
                    )}
                  </span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </Box>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ flex: 1, justifyContent: "flex-end" }}
            >
              <TextField
                type="date"
                size="small"
                label={intl.formatMessage({ id: "dashboardClient.trajet.filters.dateLabel" })}
                InputLabelProps={{ shrink: true }}
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                sx={{ minWidth: { xs: "100%", md: 180 } }}
              />

              <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 200 } }}>
                <InputLabel id="trajet-status-label">
                  {intl.formatMessage({ id: "dashboardClient.trajet.filters.statusLabel" })}
                </InputLabel>
                <Select
                  labelId="trajet-status-label"
                  label={intl.formatMessage({ id: "dashboardClient.trajet.filters.statusLabel" })}
                  value={selectedStatus}
                  onChange={(event) => setSelectedStatus(event.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder={intl.formatMessage({ id: "dashboardClient.trajet.filters.searchPlaceholder" })}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ color: "#90a4b8" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: "100%", md: 260 } }}
              />

              <Button
                variant="outlined"
                startIcon={<FilterAltOutlined />}
                onClick={loadPassengerRides}
                sx={{ minHeight: 40, whiteSpace: "nowrap" }}
              >
                {intl.formatMessage({ id: "dashboardClient.trajet.filters.filterButton" })}
              </Button>

              <Button
                variant="contained"
                startIcon={<RefreshOutlined />}
                onClick={loadPassengerRides}
                sx={{
                  minHeight: 40,
                  whiteSpace: "nowrap",
                  background: "linear-gradient(180deg, #0f6a3b, #0a532e)",
                }}
              >
                {intl.formatMessage({ id: "dashboardClient.trajet.filters.refreshButton" })}
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
              gap: 1.5,
              mt: 2.25,
            }}
          >
            <StatCard
              label={intl.formatMessage({ id: "dashboardClient.trajet.stats.total.label" })}
              value={totalTrips}
              helper={intl.formatMessage({ id: "dashboardClient.trajet.stats.total.helper" }, { count: filteredTripsCount })}
              icon={<DirectionsCarOutlined />}
            />
            <StatCard
              label={intl.formatMessage({ id: "dashboardClient.trajet.stats.upcoming.label" })}
              value={upcomingTrips.length}
              helper={intl.formatMessage({ id: "dashboardClient.trajet.stats.upcoming.helper" })}
              icon={<RouteOutlined />}
            />
            <StatCard
              label={intl.formatMessage({ id: "dashboardClient.trajet.stats.past.label" })}
              value={pastTrips.length}
              helper={intl.formatMessage({ id: "dashboardClient.trajet.stats.past.helper" })}
              icon={<AccessTimeOutlined />}
            />
            <StatCard
              label={intl.formatMessage({ id: "dashboardClient.trajet.stats.status.label" })}
              value={selectedStatus === "all" ? intl.formatMessage({ id: "dashboardClient.trajet.status.all" }) : activeStatusLabel}
              helper={intl.formatMessage(
                { id: "dashboardClient.trajet.stats.status.helper" },
                { pending: pendingCount, confirmed: confirmedCount, completed: completedCount }
              )}
              icon={<PaidOutlined />}
            />
          </Box>
        </Paper>

        {loadError ? <Alert severity="error">{loadError}</Alert> : null}

        {isLoading ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: 4,
              border: "1px solid rgba(15, 39, 72, 0.08)",
              display: "grid",
              placeItems: "center",
              gap: 2,
              background: "#fff",
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ color: "#64748b", fontWeight: 700 }}>
              {intl.formatMessage({ id: "dashboardClient.trajet.loading.trips" })}
            </Typography>
          </Paper>
        ) : null}

        {!isLoading && !loadError && filteredTripsCount === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: { xs: 3, md: 5 },
              border: "1px solid rgba(15, 39, 72, 0.08)",
              background: "#fff",
              textAlign: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#10233f" }}>
              {intl.formatMessage({ id: "dashboardClient.trajet.empty.title" })}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "#64748b", fontWeight: 600 }}>
              {intl.formatMessage({ id: "dashboardClient.trajet.empty.subtitle" })}
            </Typography>
          </Paper>
        ) : null}

        {!isLoading && !loadError ? (
          <>
            <TripsTableSection
              title={intl.formatMessage({ id: "dashboardClient.trajet.tabs.view.upcoming" })}
              items={visibleTrips.upcoming}
              onOpenDetails={openDetails}
              onOpenTicket={openBookingTicket}
              ticketLoadingId={ticketLoadingId}
              loading={isLoading}
            />
            <TripsTableSection
              title={intl.formatMessage({ id: "dashboardClient.trajet.tabs.view.past" })}
              items={visibleTrips.past}
              onOpenDetails={openDetails}
              onOpenTicket={openBookingTicket}
              ticketLoadingId={ticketLoadingId}
              loading={isLoading}
            />
          </>
        ) : null}

        <TripDetailsDialog open={detailsOpen} trip={selectedTrip} timezone={timezone} onClose={closeDetails} />
        <CreateTripDialog open={createTripOpen} onClose={closeCreateTrip} timezone={timezone} onCreated={loadPassengerRides} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#6a778a", fontSize: 12, fontWeight: 700 }}>
        <span>{intl.formatMessage({ id: "dashboardClient.trajet.lastUpdated" }, { date: lastUpdatedLabel })}</span>
      </Box>
    </>
  );
}
